import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixBookingsDisplaySettingsV1BookingsDisplaySettingsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/display-settings',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_bookings_display-settings';

/**
 * A single-item getter addressed by the natural key (app_id + widget_type
 * [+ custom_widget_key]); annotated as the GET_ITEM CRUD flavor. The standard
 * id-path Get is intentionally not exposed — `id` is server-derived (see
 * TECH_DESIGN D5/D7).
 */
export function getBookingsDisplaySettingsByWidget(
  payload: object
): RequestOptionsFactory<any> {
  function __getBookingsDisplaySettingsByWidget({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.display_settings.v1.bookings_display_settings',
      method: 'GET' as any,
      methodFqn:
        'wix.bookings.display_settings.v1.BookingsDisplaySettingsService.GetBookingsDisplaySettingsByWidget',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsDisplaySettingsV1BookingsDisplaySettingsServiceUrl(
        {
          protoPath:
            '/v1/bookings-display-settings/app/{appId}/widget-type/{widgetType}',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'bookingsDisplaySettings.createdDate' },
              { path: 'bookingsDisplaySettings.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getBookingsDisplaySettingsByWidget;
}

/**
 * Atomic insert-or-update of the BookingsDisplaySettings row identified by
 * (app_id, widget_type [+ custom_widget_key for CUSTOM]); the site is implicit.
 * Single Save action on the client.
 *
 * Keyed by the natural key: if no row matches it is created, otherwise it is
 * updated in place (partial update via `field_mask`). `revision` is
 * server-assigned and read-only — Upsert does not use optimistic-concurrency
 * control (that belongs to Update / AIP-4006), so concurrent writes to the same
 * row are last-write-wins.
 *
 * `extended_fields` are validated at write time against the JSON Schema the app
 * registered for this entity in Dev Center; a payload that violates that schema
 * is rejected with `INVALID_EXTENDED_FIELDS`.
 */
export function upsertBookingsDisplaySettings(
  payload: object
): RequestOptionsFactory<any> {
  function __upsertBookingsDisplaySettings({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'bookingsDisplaySettings.createdDate' },
          { path: 'bookingsDisplaySettings.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.bookings.display_settings.v1.bookings_display_settings',
      method: 'POST' as any,
      methodFqn:
        'wix.bookings.display_settings.v1.BookingsDisplaySettingsService.UpsertBookingsDisplaySettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsDisplaySettingsV1BookingsDisplaySettingsServiceUrl(
        {
          protoPath:
            '/v1/bookings-display-settings/upsert/app/{bookingsDisplaySettings.appId}/widget-type/{bookingsDisplaySettings.widgetType}',
          data: serializedData,
          host,
        }
      ),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'bookingsDisplaySettings.createdDate' },
              { path: 'bookingsDisplaySettings.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __upsertBookingsDisplaySettings;
}

/**
 * Deletes the BookingsDisplaySettings row with the given ID, resetting that
 * widget to its server defaults: a subsequent GetBookingsDisplaySettingsByWidget
 * returns a freshly synthesised default row (with no `revision`).
 *
 * Use the `id` from a previous Upsert response, or from a
 * GetBookingsDisplaySettingsByWidget response for a saved row (a synthesised
 * default row carries no `id`). Idempotent: deleting a row that isn't stored is
 * a no-op success — the widget is already at its defaults.
 */
export function deleteBookingsDisplaySettings(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteBookingsDisplaySettings({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.display_settings.v1.bookings_display_settings',
      method: 'DELETE' as any,
      methodFqn:
        'wix.bookings.display_settings.v1.BookingsDisplaySettingsService.DeleteBookingsDisplaySettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsDisplaySettingsV1BookingsDisplaySettingsServiceUrl(
        {
          protoPath:
            '/v1/bookings-display-settings/{bookingsDisplaySettingsId}',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteBookingsDisplaySettings;
}
